import { useSanitizedRouteParams } from 'src/hooks/useSanitizedRouteParams';
import { Button, Space } from 'antd';
import { useTranslation } from 'react-i18next';

import SuspenseLoader from 'src/components/SuspenseLoader';
import TabPages from 'src/components/TabPages/TabPages';
import { useGlobal } from 'src/contexts/GlobalContext';

import { useSiteDetails } from './SiteDetailsContext';
import Campaigns from './tabPages/Campaigns';
import Design from './tabPages/Design';
import Settings from './tabPages/Settings';
import Stylesheet from './tabPages/Stylesheet';

type TabKey = 'settings' | 'design' | 'stylesheet' | 'campaigns';

interface TabItem {
  key: TabKey;
  label: string;
  Component: React.ReactNode;
}

export const SITE_TABS: TabItem[] = [
  {
    key: 'settings',
    label: 'site.settings',
    Component: <Settings />,
  },
  {
    key: 'design',
    label: 'site.design.title',
    Component: <Design />,
  },
  {
    key: 'stylesheet',
    label: 'site.stylesheet.title',
    Component: <Stylesheet />,
  },
  {
    key: 'campaigns',
    label: 'site.campaigns.title',
    Component: <Campaigns />,
  },
];

export const SiteDetailsTab = () => {
  const { tab } = useSanitizedRouteParams();
  const {
    site,
    isLoading,
    formMethods,
    handleSave,
    isUpdateSitePending,
    handleCancel,
    handleNavigateBack,
    handleUpdateName,
    handleTabChange,
  } = useSiteDetails();
  const { t } = useTranslation();
  const { isDesktop } = useGlobal();

  const { isDirty: isFormDirty, dirtyFields } = formMethods.formState;
  const isDirty = Object.keys(dirtyFields).length > 0 && isFormDirty;

  const isCampaignsTab = tab === 'campaigns';

  const action = isDesktop && !isCampaignsTab && (
    <Space>
      <Button onClick={handleCancel} disabled={!isDirty}>
        {t('site.cancel')}
      </Button>
      <Button type="primary" onClick={handleSave} disabled={!isDirty} loading={isUpdateSitePending}>
        {t('site.save')}
      </Button>
    </Space>
  );

  const items = SITE_TABS.map((item) => ({
    key: item.key,
    label: t(item.label),
    Component: item.Component,
  }));

  if (isLoading) {
    return <SuspenseLoader />;
  }

  return (
    <TabPages
      tabItems={items}
      handleTabChange={(key) => handleTabChange(key)}
      tab={tab}
      handleSave={handleSave}
      handleCancel={handleCancel}
      isDirty={isDirty}
      isPending={isUpdateSitePending}
      titleName={site?.name ?? ''}
      activeTabKey={tab}
      cancelButtonText={t('general.cancel')}
      saveButtonText={t('site.general.saveSite')}
      action={action}
      handleNavigateBack={handleNavigateBack}
      handleUpdateName={handleUpdateName}
      handleReset={handleCancel}
    />
  );
};
